/**
 * Copyright 2019 CANAL+ Group
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Subject } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import DASHFeature from "../../../../../transports/dash";
import SMOOTHFeature from "../../../../../transports/smooth";
import { createManifestPipeline } from "../../../../../core/pipelines";
import { SegmentConstuctionError } from "../../utils";
/**
 * Get the TransportPipeline for current transport.
 *
 * @param transport - Transport option for current manifest.
 * @returns A instance of TransportPipelines for the current url manifest.
 *
 */
export function getTransportPipelineByTransport(transport) {
    var pipelineTypes = {
        smooth: SMOOTHFeature,
        dash: DASHFeature,
    };
    return pipelineTypes[transport]({
        lowLatencyMode: false,
    });
}
/**
 * Get the manifest from an url.
 *
 * @param manifestURL - Manifest url on the web.
 * @param transport - Transport that need to be use.
 * @returns A instance of Manifest for the current url and the
 * transportPipeline associated to it.
 *
 */
export function manifestLoader(manifestURL, transport) {
    if (transport === void 0) { transport = "dash"; }
    var transportPipelines = getTransportPipelineByTransport(transport);
    var manifestPipeline = createManifestPipeline(transportPipelines, {
        lowLatencyMode: false,
        manifestRetry: 5,
        offlineRetry: 5,
    }, new Subject());
    return manifestPipeline
        .fetch(manifestURL)
        .pipe(mergeMap(function (response) {
        return manifestPipeline
            .parse(response.value, manifestURL)
            .pipe(map(function (_a) {
            var manifest = _a.manifest;
            return ({ manifest: manifest, transportPipelines: transportPipelines });
        }));
    }));
}
/**
 * Get the adaptations for the current period.
 *
 * @param IStoredManifest - The global builder we insert in IndexDB
 * @returns An Object of period associated with an array of adaptations.
 *
 */
export function getBuilderFormattedForAdaptations(_a) {
    var builder = _a.builder;
    return Object.keys(builder).reduce(function (acc, curr) {
        var ctxs = builder[curr];
        if (ctxs == null || ctxs.length === 0) {
            return acc;
        }
        for (var i = 0; i <= ctxs.length; i++) {
            var ctx = ctxs[i];
            var periodId = ctx.period.id;
            if (acc[periodId] === undefined) {
                acc[periodId] = [];
                acc[periodId].push({
                    type: ctx.adaptation.type,
                    audioDescription: ctx.adaptation.isAudioDescription,
                    closedCaption: ctx.adaptation.isClosedCaption,
                    language: ctx.adaptation.language,
                    representations: [ctx.representation],
                });
                return acc;
            }
            acc[periodId].push({
                type: ctx.adaptation.type,
                audioDescription: ctx.adaptation.isAudioDescription,
                closedCaption: ctx.adaptation.isClosedCaption,
                language: ctx.adaptation.language,
                representations: [ctx.representation],
            });
            return acc;
        }
        return acc;
    }, {});
}
/**
 * Get the segment for the current representation.
 *
 * @param IStoredManifest - The global builder we insert in IndexDB
 * @returns An Object of representation associated with an array of segments.
 *
 */
export function getBuilderFormattedForSegments(_a) {
    var builder = _a.builder;
    return Object.keys(builder).reduce(function (acc, curr) {
        var ctxs = builder[curr];
        if (ctxs == null || ctxs.length === 0) {
            return acc;
        }
        for (var i = 0; i <= ctxs.length; i++) {
            var ctx = ctxs[i];
            var repreId = ctx.representation.id;
            acc[repreId] = ctx.nextSegments
                .reduce(function (accSegts, currSegment) {
                if (Array.isArray(currSegment)) {
                    var time = currSegment[0], timescale = currSegment[1], duration = currSegment[2];
                    accSegts.push({ time: time, timescale: timescale, duration: duration });
                    return accSegts;
                }
                return accSegts;
            }, []);
            return acc;
        }
        return acc;
    }, {});
}
export function getKeySystemsSessionsOffline(contentsProtection) {
    if (contentsProtection === undefined || contentsProtection.length === 0) {
        return undefined;
    }
    var flattenedSessionsIDS = contentsProtection
        .reduce(function (acc, curr) {
        var _a;
        acc.type = curr.keySystems.type;
        for (var i = 0; i < curr.keySystems.sessionsIDS.length; ++i) {
            (_a = acc.sessionsIDS).push.apply(_a, curr.keySystems.sessionsIDS);
        }
        return acc;
    }, { type: "", sessionsIDS: [] });
    return __assign(__assign({}, flattenedSessionsIDS), { sessionsIDS: flattenedSessionsIDS.sessionsIDS
            .filter(function (sessionID, index) {
            return flattenedSessionsIDS.sessionsIDS
                .map(function (session) { return session.initData; }).indexOf(sessionID.initData) === index;
        }) });
}
/**
 * Returns the structure of the manifest needed by the RxPlayer transport local.
 *
 * @remarks
 * It's mandatory to rebuild again the local manifest
 * when we want to play an offline content because we lose every reference
 * when storing in IndexDB.
 *
 * @param Manifest - The Manifest we downloaded when online
 * @param ISegmentStored[] - The segments we downloaded online for the current content
 * @param IAdaptationForPeriodBuilder - The Adaptations by period
 * @param number - The global duration of the content
 * @param boolean - Tell if the content is 100% complete
 * @param IDBPDatabase - An Instance of IndexDB to be able to retrieve content in base
 * @returns A ILocalManifest to the RxPlayer transport local understand
 *
 */
export function offlineManifestLoader(manifest, adaptationsBuilder, representationsBuilder, _a) {
    var contentID = _a.contentID, duration = _a.duration, isFinished = _a.isFinished, db = _a.db;
    return {
        type: "local",
        version: "0.1",
        duration: duration,
        periods: manifest.periods.map(function (period) {
            return {
                start: period.start,
                duration: period.duration !== undefined ? period.duration : 0,
                adaptations: adaptationsBuilder[period.id].map(function (adaptation) { return ({
                    type: adaptation.type,
                    audioDescription: adaptation.audioDescription,
                    closedCaption: adaptation.closedCaption,
                    language: adaptation.language,
                    representations: adaptation.representations.map(function (_a) {
                        var mimeType = _a.mimeType, codec = _a.codec, id = _a.id, representation = __rest(_a, ["mimeType", "codec", "id"]);
                        return ({
                            bitrate: representation.bitrate,
                            mimeType: mimeType !== undefined ? mimeType : "",
                            codecs: codec !== undefined ? codec : "",
                            width: representation.width,
                            height: representation.height,
                            index: {
                                loadInitSegment: function (_a) {
                                    var resolve = _a.resolve, reject = _a.reject;
                                    db.get("segments", "init--" + id + "--" + contentID)
                                        .then(function (segment) {
                                        if (segment === undefined) {
                                            return reject(new SegmentConstuctionError(contentID + ":\n                              Impossible to retrieve INIT segment in IndexDB for\n                              representation: " + id + ", got: undefined"));
                                        }
                                        return resolve({
                                            data: segment.data,
                                        });
                                    })
                                        .catch(reject);
                                },
                                loadSegment: function (_a, _b) {
                                    var reqSegmentTime = _a.time;
                                    var resolve = _b.resolve, reject = _b.reject;
                                    db.get("segments", reqSegmentTime + "--" + id + "--" + contentID)
                                        .then(function (segment) {
                                        if (segment === undefined) {
                                            return resolve({
                                                data: new Uint8Array(0),
                                            });
                                        }
                                        return resolve({
                                            data: segment.data,
                                        });
                                    })
                                        .catch(reject);
                                },
                                segments: representationsBuilder[id],
                            },
                        });
                    }),
                }); }),
            };
        }),
        isFinished: isFinished,
    };
}
